import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Order Placed', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', step: 1 },
  confirmed: { label: 'Confirmed', icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', step: 2 },
  processing: { label: 'Being Prepared', icon: Package, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', step: 2 },
  shipped: { label: 'Shipped', icon: Truck, color: 'text-perfect-peach', bg: 'bg-orange-50', border: 'border-orange-200', step: 3 },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', step: 4 },
  cancelled: { label: 'Cancelled', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', step: 0 },
};

const PAYMENT_LABELS = {
  paid: { label: 'Paid', color: 'text-green-700 bg-green-100' },
  pending: { label: 'Payment Pending', color: 'text-yellow-700 bg-yellow-100' },
  failed: { label: 'Payment Failed', color: 'text-red-700 bg-red-100' },
};

const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) return;

    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const response = await axios.post(`${API}/orders/track`, {
        order_id: orderId.trim(),
        email: email.trim(),
      });
      setOrder(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to find your order. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = order ? (STATUS_CONFIG[order.status] || STATUS_CONFIG.pending) : null;
  const paymentConfig = order ? (PAYMENT_LABELS[order.payment_status] || PAYMENT_LABELS.pending) : null;
  const currentStep = statusConfig?.step || 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Track Your Order | The Perfect Peach Club</title>
        <meta name="description" content="Track your order status from The Perfect Peach Club. Enter your order ID and email to see real-time updates." />
      </Helmet>

      <Navigation />
      <ChatWidget />

      <div className="max-w-3xl mx-auto px-5 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D2420] mb-3" data-testid="track-order-heading">
            Track Your Order
          </h1>
          <p className="text-african-earth text-base md:text-lg max-w-lg mx-auto">
            Enter your order ID and email address to check your order status
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleTrack} className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft mb-8" data-testid="track-order-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-african-earth font-medium mb-2 text-sm">Order ID</label>
              <input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="e.g. a1b2c3d4-..."
                required
                className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach text-sm"
                data-testid="track-order-id-input"
              />
            </div>
            <div>
              <label className="block text-african-earth font-medium mb-2 text-sm">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach text-sm"
                data-testid="track-order-email-input"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-perfect-peach text-white px-6 py-3.5 rounded-full font-medium text-base hover:bg-[#C57D63] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="track-order-submit-button"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Track Order
              </>
            )}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-8 flex items-start gap-3" data-testid="track-order-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Order Result */}
        {order && (
          <div className="space-y-6" data-testid="track-order-result">
            {/* Status Banner */}
            <div className={`${statusConfig.bg} ${statusConfig.border} border rounded-2xl p-5 sm:p-6`}>
              <div className="flex items-center gap-3 mb-3">
                <statusConfig.icon className={`w-6 h-6 ${statusConfig.color}`} />
                <h2 className="font-playfair text-xl sm:text-2xl font-semibold text-[#2D2420]" data-testid="order-status-label">
                  {statusConfig.label}
                </h2>
              </div>
              <p className="text-african-earth text-sm">
                Order #{order.id.slice(0, 8)}... placed on {formatDate(order.created_at)}
              </p>
            </div>

            {/* Progress Steps — hide if cancelled */}
            {order.status !== 'cancelled' && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-soft">
                <div className="flex items-center justify-between relative">
                  {/* Progress line */}
                  <div className="absolute top-4 left-0 right-0 h-0.5 bg-[#E0D5D0]" />
                  <div
                    className="absolute top-4 left-0 h-0.5 bg-perfect-peach transition-all duration-500"
                    style={{ width: `${Math.max(0, (currentStep - 1) / 3) * 100}%` }}
                  />

                  {[
                    { label: 'Placed', step: 1 },
                    { label: 'Preparing', step: 2 },
                    { label: 'Shipped', step: 3 },
                    { label: 'Delivered', step: 4 },
                  ].map((s) => (
                    <div key={s.step} className="relative flex flex-col items-center z-10">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors duration-300 ${
                          currentStep >= s.step
                            ? 'bg-perfect-peach text-white'
                            : 'bg-[#E0D5D0] text-african-earth/50'
                        }`}
                      >
                        {currentStep >= s.step ? '✓' : s.step}
                      </div>
                      <span className={`text-xs mt-2 ${currentStep >= s.step ? 'text-perfect-peach font-medium' : 'text-african-earth/50'}`}>
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Order Details Card */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-soft space-y-5">
              {/* Payment + Shipping badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${paymentConfig.color}`} data-testid="payment-status-badge">
                  {paymentConfig.label}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-african-earth bg-bone-white">
                  {order.shipping_option === 'local' ? 'Local Delivery' : 'Nationwide Courier'}
                </span>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-playfair text-lg font-semibold text-[#2D2420] mb-3">Items</h3>
                <div className="divide-y divide-[#F0EAE6]">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2.5 text-sm">
                      <span className="text-african-earth">{item.quantity}x Product</span>
                      <span className="font-medium text-[#2D2420]">R{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-[#E0D5D0] pt-4 space-y-2">
                <div className="flex justify-between text-sm text-african-earth">
                  <span>Shipping</span>
                  <span>{order.shipping_cost === 0 ? 'FREE' : `R${order.shipping_cost?.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-[#2D2420]">Total</span>
                  <span className="text-perfect-peach" data-testid="order-total">R{order.total_amount?.toFixed(2)}</span>
                </div>
              </div>

              {/* Delivery Address */}
              {order.shipping_address && (
                <div className="border-t border-[#E0D5D0] pt-4">
                  <h3 className="font-playfair text-lg font-semibold text-[#2D2420] mb-2">Delivery To</h3>
                  <p className="text-sm text-african-earth leading-relaxed">
                    {order.customer_name}<br />
                    {order.shipping_address.address}<br />
                    {order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help Text */}
        {!order && !error && (
          <div className="text-center text-african-earth/60 text-sm mt-4">
            <p>You can find your order ID in your confirmation email.</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TrackOrderPage;
